import {
  ArrowDownIcon,
  ArrowSmRightIcon,
  ArrowUpIcon,
  LinkIcon,
  PencilAltIcon,
} from "@heroicons/react/outline";
import { compact } from "lodash";
import pluralize from "pluralize";
import { Fragment, PropsWithChildren, useState } from "react";
import { TbPlaylistAdd } from "react-icons/tb";
import {
  ArtifactType,
  GoalArtifactSidebarFragmentFragment,
  GoalScope,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import ArtifactCreationDialog from "@apps/artifact-creation-dialog/artifact-creation-dialog";
import SidebarLabel from "@apps/artifact-sidebar/components/sidebar-label";
import SidebarRow from "@apps/artifact-sidebar/components/sidebar-row";
import ArtifactTeams from "@apps/artifact/components/artifact-teams";
import GoalDueDate from "@apps/artifact/components/goal-due-date";
import GoalParticipantsDialog from "@apps/artifact/components/goal-participants-dialog";
import GoalScopeComponent from "@apps/artifact/components/goal-scope";
import GoalStartDate from "@apps/artifact/components/goal-start-date";
import GoalState from "@apps/artifact/components/goal-state";
import GoalVisibilityDropdown from "@apps/artifact/components/goal-visibility";
import CheckinDialog from "@apps/checkin-dialog/checkin-dialog";
import GoalAlignmentPickerDialogWithExistingGoal from "@apps/goal-alignment-dialog-picker/goal-alignment-dialog-picker-with-existing-goal";
import GoalProgress from "@apps/goal-progress/goal-progress";
import { GoalProgressView } from "@apps/goal-progress/goal-progress-constants";
import useLabel from "@apps/use-label/use-label";
import Avatars from "@components/avatar/avatars";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import GoalIcon from "@components/goal-icon/goal-icon";
import AppLink from "@components/link/link";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull, getProgressTypeLabel } from "@helpers/helpers";

import ArtifactSidebarAlignedGoalItem from "./aligned-goal-item";
import GoalEditValues from "./components/goal-edit-values";

const SidebarContainer = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => (
  <div className={classNames("flex w-full flex-col gap-3", className)}>
    {children}
  </div>
);

const GoalParticipantRow = ({
  participants,
  canUpdate,
  onClick,
  label = "Owner",
  rowClassName = "",
}: {
  participants: BasicUser[];
  canUpdate: boolean;
  onClick: () => void;
  label: string;
  rowClassName?: string;
}) => (
  <SidebarRow className={rowClassName}>
    <SidebarLabel>{pluralize(label, 2)}</SidebarLabel>
    <div className="flex items-center gap-1 text-sm text-gray-600">
      <button
        className={classNames(
          "flex items-center gap-1 px-3 h-[32px] group rounded-md hover:bg-gray-100",
          participants.length > 0 && "pl-4",
        )}
        onClick={onClick}
        disabled={!canUpdate}
      >
        {participants.length > 0 && (
          <Avatars
            users={participants}
            max={2}
            size={5}
            modalTitle={pluralize(label, 2)}
            clickingWillShowModal={false}
            className="pl-[2px]"
          />
        )}
        {participants.length === 0 && (
          <span className="text-gray-400">None</span>
        )}
        {participants.length === 1 && <span>{participants[0].name}</span>}
        {participants.length > 1 && (
          <span className="text-gray-400">
            {participants.length}{" "}
            {pluralize(label.toLowerCase(), participants.length)}
          </span>
        )}
        {canUpdate && (
          <div>
            <PencilAltIcon
              className="w-4 h-4 text-gray-400 flex-none opacity-0 group-hover:opacity-100"
              aria-label={`Edit ${label.toLowerCase()}s button`}
            />
          </div>
        )}
      </button>
    </div>
  </SidebarRow>
);

// The goal's parameters (progress, state, dates, owners, contributors, …).
// Rendered by GoalContent, which is shared by the goal page and the drawer.
export const GoalParametersList = ({
  artifact,
}: {
  artifact: GoalArtifactSidebarFragmentFragment;
}) => {
  const label = useLabel();
  const [isShowingCheckinDialog, setIsShowingCheckinDialog] = useState(false);
  const [isEditingGoalValues, setIsEditingGoalValues] = useState(false);
  const [showParticipantDialog, setShowParticipantDialog] = useState(false);

  // Rows sit flush with the surrounding content rather than the sidebar row's
  // built-in inset, so the labels line up under the goal title.
  const rowClassName = "!px-0";

  const owners = artifact?.owners ? assertEdgesNonNull(artifact.owners) : [];
  const contributors = artifact?.contributors
    ? assertEdgesNonNull(artifact.contributors)
    : [];

  return (
    <>
      {showParticipantDialog && (
        <GoalParticipantsDialog
          artifact={artifact}
          onClose={() => setShowParticipantDialog(false)}
        />
      )}
      {isShowingCheckinDialog && (
        <CheckinDialog
          artifact={artifact}
          onClose={() => setIsShowingCheckinDialog(false)}
        />
      )}
      <SidebarContainer>
        <SidebarRow className={rowClassName}>
          <SidebarLabel>Progress</SidebarLabel>
          <div className="flex-1 pl-0 flex gap-1.5 items-center">
            <GoalProgress
              artifact={artifact}
              goalProgressView={GoalProgressView.pillAndProgressBar}
              className="flex-1 px-3 h-[32px] hover:bg-gray-100 rounded-md"
            />
          </div>
        </SidebarRow>
        <SidebarRow className={rowClassName}>
          <SidebarLabel>Progress Type</SidebarLabel>
          <div className="flex-1 flex items-center">
            <Button
              text={getProgressTypeLabel(artifact)}
              theme={buttonTheme.text}
              onClick={() => setIsEditingGoalValues(true)}
            />
            {artifact.kpi && (
              <Button
                theme={buttonTheme.iconGray}
                to={`/kpis/${artifact.kpi.id}`}
                icon={LinkIcon}
                tooltip={`Connected to KPI: ${artifact.kpi.title}`}
              />
            )}
            {isEditingGoalValues && (
              <GoalEditValues
                artifact={artifact}
                onClose={() => setIsEditingGoalValues(false)}
              />
            )}
          </div>
        </SidebarRow>
        <SidebarRow className={rowClassName}>
          <SidebarLabel>
            {label("goal", { capitalize: true })} Type
          </SidebarLabel>
          <div className="flex">
            <GoalScopeComponent artifact={artifact} />
          </div>
        </SidebarRow>
        <SidebarRow className={rowClassName}>
          <SidebarLabel>State</SidebarLabel>
          <GoalState artifact={artifact} />
        </SidebarRow>
        <SidebarRow className={rowClassName}>
          <SidebarLabel>Visibility</SidebarLabel>
          <div className="flex">
            <GoalVisibilityDropdown artifact={artifact} />
          </div>
        </SidebarRow>
        <SidebarRow className={rowClassName}>
          <SidebarLabel>Dates</SidebarLabel>
          <div className="flex items-center text-sm tracking-tight gap-1">
            <div className="group">
              <GoalStartDate artifact={artifact} />
            </div>
            <ArrowSmRightIcon className="h-4 w-4 text-gray-400" />
            <div className="group">
              <GoalDueDate artifact={artifact} />
            </div>
          </div>
        </SidebarRow>
        <GoalParticipantRow
          participants={owners}
          canUpdate={artifact.canUpdate?.permission}
          onClick={() => {
            setShowParticipantDialog(true);
          }}
          label="Owner"
          rowClassName={rowClassName}
        />
        <GoalParticipantRow
          participants={contributors}
          canUpdate={artifact.canUpdate?.permission}
          onClick={() => {
            setShowParticipantDialog(true);
          }}
          label="Contributor"
          rowClassName={rowClassName}
        />
        {artifact.scope === GoalScope.Team && (
          <SidebarRow>
            <SidebarLabel>
              {label("team", { pluralize: true, titleCase: true })}
            </SidebarLabel>
            <div
              className={classNames(
                artifact.teams?.edges.length === 0 ? "" : "pl-2",
              )}
            >
              <ArtifactTeams artifact={artifact} />
            </div>
          </SidebarRow>
        )}
      </SidebarContainer>
    </>
  );
};

// The goal alignment tree (parent/ancestor/children) plus the controls to add
// or align parent and sub goals. Rendered by GoalContent, which is shared by
// the goal page and the drawer.
export const GoalAlignmentSection = ({
  artifact,
}: {
  artifact: GoalArtifactSidebarFragmentFragment;
}) => {
  const label = useLabel();
  const [createGoalDialogAlignType, setCreateGoalDialogAlignType] = useState<
    undefined | "child" | "parent"
  >(undefined);
  const [isOpenGoalAlignmentDialog, setIsOpenGoalAlignmentDialog] = useState<
    undefined | "child" | "parent"
  >();
  const [childGoalAlignmentDialogRef, setChildGoalAlignmentPopoverRef] =
    useState<HTMLElement | null>(null);
  const [parentGoalAlignmentDialogRef, setParentGoalAlignmentPopoverRef] =
    useState<HTMLElement | null>(null);

  const basicChildGoals = artifact?.basicChildGoals
    ? assertEdgesNonNull(artifact.basicChildGoals)
    : [];
  const topLevelGoal = artifact?.topLevelGoal;

  const createDialog =
    createGoalDialogAlignType !== undefined ? (
      <ArtifactCreationDialog
        formOptions={{
          title: ``,
          artifactType: ArtifactType.Goal,
          parentGoal:
            createGoalDialogAlignType === "parent" && artifact.title
              ? {
                  value: artifact.id,
                  label: artifact.title || "",
                  htmlLabel: (
                    <div className="flex items-center gap-1">
                      <GoalIcon scope={artifact.scope} state={artifact.state} />{" "}
                      {artifact.title}
                    </div>
                  ),
                }
              : undefined,
          childGoals:
            createGoalDialogAlignType === "child" && artifact.title
              ? [
                  {
                    value: artifact.id,
                    label: artifact.title || "",
                    htmlLabel: (
                      <div className="flex items-center gap-1">
                        <GoalIcon
                          scope={artifact.scope}
                          state={artifact.state}
                        />{" "}
                        {artifact.title}
                      </div>
                    ),
                  },
                ]
              : undefined,
        }}
        customFields={compact([
          createGoalDialogAlignType === "parent" &&
            artifact.title && {
              label: "Parent",
              renderContent: () => (
                <div className="flex items-center gap-1">
                  <GoalIcon scope={artifact.scope} state={artifact.state} />
                  {artifact.title}
                </div>
              ),
            },
          createGoalDialogAlignType === "child" &&
            artifact.title && {
              label: "Child",
              renderContent: () => (
                <div className="flex items-center gap-1">
                  <GoalIcon scope={artifact.scope} state={artifact.state} />
                  {artifact.title}
                </div>
              ),
            },
        ])}
        hideTypeSelector
        onClose={() => setCreateGoalDialogAlignType(undefined)}
      />
    ) : null;

  const viewLink =
    artifact.parentGoal || basicChildGoals.length > 0 ? (
      <AppLink
        className="text-blue-link hover:underline text-xs tracking-tight mr-1"
        to={`/goal-alignment?goalId=${artifact.id}`}
      >
        View full alignment
      </AppLink>
    ) : undefined;

  const alignmentTitle = `${label("goal", { capitalize: true })} Alignment`;

  const body = (
    <>
      {!artifact.parentGoal &&
        artifact.canUpdate.permission &&
        !artifact.parentGoal && (
          <div className="mb-2 flex">
            <Dropdown
              options={compact([
                {
                  label: `Create parent ${label("goal")}`,
                  onClick: () => setCreateGoalDialogAlignType("child"),
                },
                artifact.canUpdate.permission && {
                  label: `Align existing ${label("goal")}`,
                  onClick: () => setIsOpenGoalAlignmentDialog("parent"),
                },
              ])}
            >
              {/* span carries the popover anchor ref since Button does not
                  forward refs */}
              <span ref={setParentGoalAlignmentPopoverRef}>
                <Button size={ButtonSize.medium} theme={buttonTheme.text}>
                  <TbPlaylistAdd className="w-5 h-5 text-gray-500" />
                  Add parent {label("goal")}
                </Button>
              </span>
            </Dropdown>
            {artifact.canUpdate.permission &&
              isOpenGoalAlignmentDialog &&
              parentGoalAlignmentDialogRef &&
              isOpenGoalAlignmentDialog === "parent" && (
                <GoalAlignmentPickerDialogWithExistingGoal
                  artifactId={artifact.id}
                  alignmentType="parent"
                  onClose={() => setIsOpenGoalAlignmentDialog(undefined)}
                  externalReferenceElement={parentGoalAlignmentDialogRef}
                />
              )}
          </div>
        )}

      <div className="empty:hidden relative">
        {topLevelGoal && topLevelGoal.id !== artifact.parentGoal?.id && (
          <>
            <ArtifactSidebarAlignedGoalItem
              key={topLevelGoal.id}
              artifact={artifact}
              alignedGoal={topLevelGoal}
              alignmentType="ancestor"
            />
            {/* ml-[13px] centers the 2px dotted line on the goal cards'
                leading icon, matching the arrows below */}
            <div className="border-l-2 border-gray-300 border-dotted h-3 ml-[13px] my-2" />
          </>
        )}
        {artifact.parentGoal && (
          <>
            <ArtifactSidebarAlignedGoalItem
              artifact={artifact}
              alignedGoal={artifact.parentGoal}
              alignmentType="parent"
            />
            {/* ml-2 centers the 12px arrow on the goal cards' 16px leading
                icon (6px card padding + 8px to icon center) */}
            <ArrowUpIcon className="h-3 w-3 text-gray-400 ml-2 my-2" />
          </>
        )}
        <ArtifactSidebarAlignedGoalItem
          key={artifact.id}
          artifact={artifact}
          alignedGoal={artifact}
        />

        {basicChildGoals.length > 0 && (
          <ArrowDownIcon className="h-3 w-3 text-gray-400 ml-2 my-2" />
        )}
        {basicChildGoals.length > 0 &&
          basicChildGoals.map((childGoal) => (
            <Fragment key={childGoal.id}>
              <ArtifactSidebarAlignedGoalItem
                artifact={artifact}
                alignedGoal={childGoal}
                alignmentType="child"
              />
              <div className="h-1.5" />
            </Fragment>
          ))}
      </div>
      {artifact.canUpdate.permission && (
        <div className="mt-2 flex">
          <Dropdown
            options={compact([
              {
                label: `Create sub ${label("goal")}`,
                onClick: () => setCreateGoalDialogAlignType("parent"),
              },
              artifact.canUpdate.permission && {
                label: `Align existing ${label("goal")}`,
                onClick: () => setIsOpenGoalAlignmentDialog("child"),
              },
            ])}
          >
            {/* span carries the popover anchor ref since Button does not
                forward refs */}
            <span ref={setChildGoalAlignmentPopoverRef}>
              <Button size={ButtonSize.medium} theme={buttonTheme.text}>
                <TbPlaylistAdd className="w-5 h-5 text-gray-500" />
                Add sub {label("goal")}
              </Button>
            </span>
          </Dropdown>
          {isOpenGoalAlignmentDialog &&
            childGoalAlignmentDialogRef &&
            isOpenGoalAlignmentDialog === "child" && (
              <GoalAlignmentPickerDialogWithExistingGoal
                artifactId={artifact.id}
                alignmentType="child"
                onClose={() => setIsOpenGoalAlignmentDialog(undefined)}
                externalReferenceElement={childGoalAlignmentDialogRef}
              />
            )}
        </div>
      )}
    </>
  );

  return (
    <>
      {createDialog}
      <div className="w-full flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <div className="flex justify-between items-center mb-2">
            <div className="tf-header-base-dark">{alignmentTitle}</div>
            <div className="flex items-center gap-2 empty:hidden">
              {viewLink}
            </div>
          </div>
          {body}
        </div>
      </div>
    </>
  );
};
